import { IEntityChildren, IEntityDescription1, IPPVision } from '../types';
import { AppGlobals } from '../AppGlobals';
import { EntityService } from './EntityService';

export interface ReadVisionResult {
    vision: IPPVision | null;
    isCurrentFound: boolean; // true if CurrentVision was matched
}


export class PPVisionService extends EntityService<IPPVision> {

    constructor() {
        super(`/PPVisions`);
    }

    public readAllForLookup(includeClosed?: boolean): Promise<IPPVision[]> {
        return this.readAll(
            `?$select=ID,Title`
            + `&$orderby=Title&$filter=CustomerID eq ${AppGlobals.CustomerID}`
        );
    }


    public async readSingleEntity(
        mode: "current" | "next" | "previous" | "byId",
        visionID?: number
    ): Promise<ReadVisionResult> {
        let filter = `CustomerID eq ${AppGlobals.CustomerID}`;
        let orderby = "Title";

        const buildQuery = (filter: string, orderby: string) =>
            `?$select=ID,Title,Description,CurrentVision`
            + `&$orderby=${orderby}`
            + `&$filter=${filter}`
            + `&$expand=PPVisionObjectives($select=ID)`
            + `&$top=1`;

        let query = "";

        switch (mode) {
            case "current":
                query = buildQuery(`${filter} and CurrentVision eq true`, "ID asc");
                break;

            case "next":
                if (!visionID) throw new Error("visionID is required for 'next' mode");
                query = buildQuery(`${filter} and ID gt ${visionID}`, "ID asc");
                break;

            case "previous":
                if (!visionID) throw new Error("visionID is required for 'previous' mode");
                query = buildQuery(`${filter} and ID lt ${visionID}`, "ID desc");
                break;

            case "byId":
                if (!visionID) throw new Error("visionID is required for 'byId' mode");
                query = buildQuery(`${filter} and ID eq ${visionID}`, "ID asc");
                break;
        }

        let visions = await this.readAll(query);
        let isCurrentFound = true;

        // Fallback if no CurrentVision exists
        if (mode === "current" && (!visions || visions.length === 0)) {
            const fallbackQuery = buildQuery(filter, "ID asc");
            visions = await this.readAll(fallbackQuery);
            isCurrentFound = false; // 👈 mark that no CurrentVision was found
        }

        return {
            vision: visions && visions.length > 0 ? visions[0] : null,
            isCurrentFound
        };
    }



    public async readTitleForVision(entityPlural: string, entitySingular: string, visionID: number): Promise<IEntityDescription1> {
        const entityDescription1: IEntityDescription1 = {
            Description1ForList: '',
            Description1ForForm: '',
            DescriptionLine1: '',
            DescriptionLine2: '',
        };
        const res = await this.readAll(`?$filter=ID eq ${visionID}&$select=Title`);
        if (res.length > 0) {
            entityDescription1.DescriptionLine1 = `${entityPlural} for vision`;
            entityDescription1.DescriptionLine2 = res[0].Title;
            entityDescription1.Description1ForList = `${entityPlural} for vision <span style='font-style: italic'>${res[0].Title}</span>`;
            entityDescription1.Description1ForForm = `${entitySingular} for vision <span style='font-style: italic'>${res[0].Title}</span>`;
        }

        return entityDescription1;
    }
}
